/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { CargoItemsDaoService } from '../cargo-item-dao.service';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { CargoItem } from 'src/app/_model/cargo-item';
import { LocalStatusService } from 'src/app/_services/local-status/local-status.service';
import { LocalCargoItemDbTableDefinition } from './local-cargo-item-db-table-definition';
import { CargoItemDbTableDefinition } from './cargo-item-db-table-definition';

@Injectable()
export class CargoItemsDbDaoService implements CargoItemsDaoService {
  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private localStatusService: LocalStatusService,
    private cargoItemTableDefinition: CargoItemDbTableDefinition,
    private localCargoItemTableDefinition: LocalCargoItemDbTableDefinition
  ) {}

  public async saveCargoItems(cargoItems: CargoItem[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var cargoItemsQueries: Query[] = [];
      for (const cargoItem of cargoItems) {
        let cargoItemQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            (cargoItem.isLocal()
              ? this.localCargoItemTableDefinition.tableName
              : this.cargoItemTableDefinition.tableName) +
            '(id, label, databag, extId, name, sortKey, serialNumber, amtdlv,' + // 8
            'amtopen, amtreturn, itemvalue, servicejobid, servicejoblabel, branchid,' + // 6
            'branchlabel, tourid, tourlabel, cargoitemtypeid, cargoitemtypelabel)' + // 3
            ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
          parameters: [
            Number(
              cargoItem.isLocal()
                ? cargoItem.id.secondaryId
                : cargoItem.id.imsId
            ),
            cargoItem.label,
            cargoItem.databag ? JSON.stringify(cargoItem.databag) : undefined,
            cargoItem.extId,
            cargoItem.name,
            cargoItem.sortKey,
            cargoItem.serialNumber,
            cargoItem.amtdlv,
            cargoItem.amtopen,
            cargoItem.amtreturn,
            cargoItem.itemvalue,
            cargoItem.servicejob
              ? Number(cargoItem.servicejob.getImsId())
              : undefined,
            cargoItem.servicejob ? cargoItem.servicejob.label : undefined,
            cargoItem.branch ? Number(cargoItem.branch.getImsId()) : undefined,
            cargoItem.branch ? cargoItem.branch.label : undefined,
            cargoItem.tour ? Number(cargoItem.tour.getImsId()) : undefined,
            cargoItem.tour ? cargoItem.tour.label : undefined,
            cargoItem.cargoItemType
              ? Number(cargoItem.cargoItemType.getImsId())
              : undefined,
            cargoItem.cargoItemType ? cargoItem.cargoItemType.label : undefined,
          ],
        };
        await this.localStatusService.storeLocalCargoItemStatus(cargoItem);
        cargoItemsQueries.push(cargoItemQuery);
      }
      await this.dbPersistenceService.executeBatch(db, cargoItemsQueries);
    } catch (error) {
      this.logger.log(
        'Error saving cargo items to db',
        error,
        '-',
        error.message
      );
    }
  }

  public async getCargoItems(): Promise<CargoItem[]> {
    let db = await this.dbPersistenceService.getImsDb();
    let cargoItems: CargoItem[] = [];

    try {
      // Get Synced CargoItems
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.cargoItemTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        let localStatus =
          await this.localStatusService.loadLocalCargoItemStatus({
            imsId: row['id'],
            secondaryId: undefined,
            objRef: 'ims/CargoItem',
          });
        cargoItems.push(
          CargoItem.fromDto(
            {
              id: row['id'],
              secondaryid: undefined,
              label: row['label'],
              extid: row['extId'],
              name: row['name'],
              sortkey: row['sortKey'],
              serialnumber: row['serialNumber'],
              amtdlv: row['amtdlv'],
              amtopen: row['amtopen'],
              amtreturn: row['amtreturn'],
              itemvalue: row['itemvalue'],
              servicejob: {
                id: row['servicejobid'],
                label: row['servicejoblabel'],
              },
              branch: {
                id: row['branchid'],
                label: row['branchlabel'],
              },
              tour: {
                id: row['tourid'],
                label: row['tourlabel'],
              },
              cargoitemtype: {
                id: row['cargoitemtypeid'],
                label: row['cargoitemtypelabel'],
              },
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
            localStatus
          )
        );
      }
    } catch (error) {
      this.logger.log('Error reading cargo items from db', error);
    }

    try {
      // Get Local CargoItems
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.localCargoItemTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        cargoItems.push(
          CargoItem.fromDto(
            {
              id: undefined,
              secondaryid: row['id'],
              label: row['label'],
              extid: row['extId'],
              name: row['name'],
              sortkey: row['sortKey'],
              serialnumber: row['serialNumber'],
              amtdlv: row['amtdlv'],
              amtopen: row['amtopen'],
              amtreturn: row['amtreturn'],
              itemvalue: row['itemvalue'],
              servicejob: {
                id: row['servicejobid'],
                label: row['servicejoblabel'],
              },
              branch: {
                id: row['branchid'],
                label: row['branchlabel'],
              },
              tour: {
                id: row['tourid'],
                label: row['tourlabel'],
              },
              cargoitemtype: {
                id: row['cargoitemtypeid'],
                label: row['cargoitemtypelabel'],
              },
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined
          )
        );
      }
    } catch (error) {
      this.logger.log('Error reading cargo items from db', error);
    }

    return cargoItems;
  }

  public async createCargoItem(cargoItem: CargoItem): Promise<CargoItem> {
    this.logger.log(this.constructor.name, 'create cargoItem:', cargoItem);
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let parameters = [
        cargoItem.label,
        cargoItem.databag ? JSON.stringify(cargoItem.databag) : undefined,
        cargoItem.extId,
        cargoItem.name,
        cargoItem.sortKey,
        cargoItem.serialNumber,
        cargoItem.amtdlv,
        cargoItem.amtopen,
        cargoItem.amtreturn,
        cargoItem.itemvalue,
        cargoItem.servicejob
          ? Number(cargoItem.servicejob.getImsId())
          : undefined,
        cargoItem.servicejob ? cargoItem.servicejob.label : undefined,
        cargoItem.branch ? Number(cargoItem.branch.getImsId()) : undefined,
        cargoItem.branch ? cargoItem.branch.label : undefined,
        cargoItem.tour ? Number(cargoItem.tour.getImsId()) : undefined,
        cargoItem.tour ? cargoItem.tour.label : undefined,
        cargoItem.cargoItemType
          ? Number(cargoItem.cargoItemType.getImsId())
          : undefined,
        cargoItem.cargoItemType ? cargoItem.cargoItemType.label : undefined,
      ];
      let values = '(?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)';
      let fields =
        '(label, databag, extId, name, sortKey, serialNumber, amtdlv,' + // 8
        'amtopen, amtreturn, itemvalue, servicejobid, servicejoblabel, branchid,' + // 6
        'branchlabel, tourid, tourlabel, cargoitemtypeid, cargoitemtypelabel)';
      let table = this.localCargoItemTableDefinition.tableName;

      let ciQuery: Query = {
        statement:
          'INSERT OR REPLACE INTO ' +
          table +
          ' ' +
          fields +
          ' VALUES ' +
          values,
        parameters: parameters,
      };

      await this.dbPersistenceService.executeStatement(db, ciQuery);
      let res = await this.dbPersistenceService.executeQuery(db, {
        statement: 'select last_insert_rowid() as id',
        parameters: [],
      });
      if (res && res.values) {
        const id = res.values.shift()?.id;
        cargoItem.id.secondaryId = id;
        cargoItem.databag.id = id;
        this.logger.log('Updated cargoItem secondary id', cargoItem);
      }
      return cargoItem;
    } catch (error) {
      this.logger.log(
        'Error saving cargoItem to db',
        error,
        '-',
        error.message
      );
      return undefined;
    }
  }

  public async reset() {
    // do nothing
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.cargoItemTableDefinition.tableName
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localCargoItemTableDefinition.tableName
    );
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.cargoItemTableDefinition.tableName
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.cargoItemTableDefinition.tableName
    );
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localCargoItemTableDefinition.tableName
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localCargoItemTableDefinition.tableName
    );
  }
}
